import React, { useEffect, useState } from 'react';

import { Close, ArrowForwardIos, ArrowBackIosNew } from '@mui/icons-material';
import { Box, Dialog, IconButton, Tooltip } from '@mui/material';

import ContainerLoadingIndicator from 'src/components/common/loading/ContainerLoadingIndicator';
import { IAssetViewerProps } from 'src/features/patient_asset/domain/types';
import { PATIENT_ASSET_LOADING_KEY } from 'src/features/patient_asset/store';

const AssetViewer: React.FC<IAssetViewerProps> = ({
	openAsset,
	onClose,
	assets,
	currentIndex,
	onIndexChange,
}) => {
	const [isLoading, setIsLoading] = useState(true);

	const currentAsset = assets[currentIndex];

	const handleNext = () => {
		if (currentIndex < assets.length - 1) {
			onIndexChange(currentIndex + 1);
		}
	};

	const handlePrev = () => {
		if (currentIndex > 0) {
			onIndexChange(currentIndex - 1);
		}
	};

	useEffect(() => {
		if (assets.length > 0) {
			onIndexChange(currentIndex);
		}
	}, [assets, onIndexChange, currentIndex]);

	useEffect(() => {
		setIsLoading(true);
	}, [currentAsset]);

	if (!openAsset) return null;
	if (!currentAsset.thumbnail_url) return null;

	return (
		<Dialog
			open={openAsset}
			onClose={onClose}
			maxWidth={false}
			fullScreen
			PaperProps={{
				sx: {
					backgroundColor: '#333',
					margin: 0,
					borderRadius: 0,
				},
			}}
		>
			{/* Close Button */}
			<IconButton
				aria-label='close'
				onClick={onClose}
				sx={{
					position: 'fixed',
					right: 20,
					top: 20,
					color: 'rgba(255, 255, 255, 0.8)',
					zIndex: 1000,
					backgroundColor: 'rgba(0, 0, 0, 0.3)',
					'&:hover': {
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
					},
				}}
			>
				<Tooltip placement='bottom-end' title='Close'>
					<Close />
				</Tooltip>
			</IconButton>

			{/* Main content area */}
			<Box
				sx={{
					width: '100vw',
					height: '100vh',
					backgroundColor: '#333',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: '20px 80px',
				}}
			>
				<Box
					sx={{
						width: '100%',
						height: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: '#333',
						position: 'absolute',
					}}
				>
					<ContainerLoadingIndicator
						loadingKey={PATIENT_ASSET_LOADING_KEY}
						manualLoading={isLoading}
					/>

					<img
						src={currentAsset.url}
						onLoad={() => setIsLoading(false)}
						style={{
							maxWidth: '100%',
							maxHeight: '100%',
							objectFit: 'contain',
							display: isLoading ? 'none' : 'block',
							pointerEvents: 'none',
							userSelect: 'none',
						}}
						alt='Asset'
					/>
				</Box>
			</Box>

			{assets.length > 1 && currentIndex < assets.length - 1 && (
				<IconButton
					onClick={handleNext}
					sx={{
						position: 'fixed',
						right: 20,
						top: '50%',
						transform: 'translateY(-50%)',
						backgroundColor: 'rgba(255, 255, 255, 0.9)',
						color: '#333',
						width: 55,
						height: 55,
						zIndex: 999,
						'&:hover': {
							backgroundColor: 'white',
						},
					}}
				>
					<ArrowForwardIos />
				</IconButton>
			)}
			{assets.length > 1 && currentIndex > 0 && (
				<IconButton
					onClick={handlePrev}
					sx={{
						position: 'fixed',
						left: 20,
						top: '50%',
						transform: 'translateY(-50%)',
						backgroundColor: 'rgba(255, 255, 255, 0.9)',
						color: '#333',
						width: 55,
						height: 55,
						zIndex: 999,
						'&:hover': {
							backgroundColor: 'white',
						},
					}}
				>
					<ArrowBackIosNew />
				</IconButton>
			)}
		</Dialog>
	);
};

export default AssetViewer;
